import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getMessagingToken, iosNeedsInstall, listenForeground } from "../lib/fcm";

const TOKEN_KEY = "konattu_fcm_token";

/**
 * Compact "Allow notifications" control rendered inside the user dropdown.
 * Three states:
 *   • disabled (default)    — shows BellOff and "Allow notifications"
 *   • enabled               — shows BellRing in red and "Notifications on"
 *   • blocked / unsupported — disabled control with explanatory text
 *
 * iOS Safari that hasn't been installed as a PWA shows the install-instructions
 * hint inline.
 */
export default function NotificationsToggle({ compact = false }) {
  const [status, setStatus] = useState("loading"); // loading|enabled|disabled|blocked|unsupported|ios-needs-install
  const [busy, setBusy] = useState(false);

  // Initial state — check server for an active token AND match against browser permission.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (iosNeedsInstall()) {
          if (!cancelled) setStatus("ios-needs-install");
          return;
        }
        if (typeof window === "undefined" || !("Notification" in window)) {
          if (!cancelled) setStatus("unsupported");
          return;
        }
        if (Notification.permission === "denied") {
          if (!cancelled) setStatus("blocked");
          return;
        }
        const res = await api.get("/push/status").catch(() => ({ data: { enabled: false } }));
        if (cancelled) return;
        const enabled = !!res.data?.enabled && Notification.permission === "granted";
        setStatus(enabled ? "enabled" : "disabled");
      } catch {
        if (!cancelled) setStatus("disabled");
      }
    })();
    // Foreground notification handler — show a toast for in-app push.
    listenForeground(({ title, body, url }) => {
      toast(title, {
        description: body,
        action: url ? { label: "Open", onClick: () => (window.location.href = url) } : undefined,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await getMessagingToken();
      if (res.status === "ios-needs-install") {
        toast.warning("On iPhone, install the app to your home screen first (Share → Add to Home Screen), then enable notifications.");
        setStatus("ios-needs-install");
        return;
      }
      if (res.status === "unsupported") {
        toast.error("This browser does not support push notifications.");
        setStatus("unsupported");
        return;
      }
      if (res.status === "denied" || res.status === "blocked") {
        toast.error("Notifications blocked. Enable them in your browser settings, then try again.");
        setStatus("blocked");
        return;
      }
      if (!res.token) {
        toast.error("Could not get a notification token. Please retry.");
        return;
      }
      // Persist to backend
      await api.post("/push/subscribe", {
        token: res.token,
        platform: "web",
        user_agent: navigator.userAgent.substring(0, 200),
      });
      localStorage.setItem(TOKEN_KEY, res.token);
      setStatus("enabled");
      toast.success("Notifications enabled. You'll get alerts for announcements & HR updates.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        await api.delete("/push/unsubscribe", { data: { token } }).catch(() => {});
        localStorage.removeItem(TOKEN_KEY);
      }
      setStatus("disabled");
      toast.success("Notifications disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable");
    } finally {
      setBusy(false);
    }
  };

  const renderBody = () => {
    if (status === "loading") return <span className="inline-flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</span>;
    if (status === "enabled") return (
      <span className="inline-flex items-center gap-2 text-sm">
        <BellRing className="h-4 w-4 text-red-600" /> Notifications on
        <span className="ml-auto text-xs text-neutral-500 group-hover:text-red-600">Turn off</span>
      </span>
    );
    if (status === "disabled") return (
      <span className="inline-flex items-center gap-2 text-sm">
        <Bell className="h-4 w-4 text-neutral-700" /> Allow notifications
      </span>
    );
    if (status === "blocked") return (
      <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
        <BellOff className="h-4 w-4" /> Blocked — enable in browser settings
      </span>
    );
    if (status === "ios-needs-install") return (
      <span className="inline-flex items-start gap-2 text-sm text-neutral-700">
        <Smartphone className="h-4 w-4 mt-0.5 shrink-0" />
        <span className="flex-1">
          <span className="font-medium text-neutral-900">Install app first</span>
          <span className="block text-[11px] text-neutral-500 mt-0.5">
            Tap <strong>Share</strong> → <strong>Add to Home Screen</strong>, then re-open the app to enable notifications.
          </span>
        </span>
      </span>
    );
    return (
      <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
        <BellOff className="h-4 w-4" /> Not supported on this browser
      </span>
    );
  };

  const clickable = status === "enabled" || status === "disabled";
  const onClick = busy ? undefined : status === "enabled" ? disable : status === "disabled" ? enable : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || !clickable}
      className={`group w-full text-left px-3 py-2 rounded-md transition-colors ${clickable ? "hover:bg-neutral-100 cursor-pointer" : "cursor-default opacity-90"} ${compact ? "" : "block"}`}
      data-testid="notifications-toggle"
    >
      {busy ? <span className="inline-flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Updating…</span> : renderBody()}
    </button>
  );
}
